import {
  createExportEnvelope,
  createExportEnvelopeFile,
  evaluateImportExecution,
  exportPayloadSchema,
  importPreviewSchema,
  parseExportEnvelopeFile,
  replaceImportPlanSchema,
  replacementDatasetEpoch,
  sha256CanonicalJson,
  type BuildMetadata,
  type ExportPayload,
  type ImmutableCheckpoint,
  type JsonValue,
  type VerifiedExportFile,
} from '@der-maschinfuehrer/contracts';

interface LocalBackupPayloadSnapshot {
  readonly payload: ExportPayload;
  readonly covered_mutation_generation: string;
}

type LocalReplacementCheckpointEvidence = ImmutableCheckpoint & {
  readonly checkpoint_kind: 'local_immutable_outside_active_db';
};

export const MAX_LOCAL_ALPHA_BACKUP_BYTES = 32 * 1024 * 1024;

export type JsonBackupCounts = Readonly<Record<string, number>>;

export interface JsonBackupIssue {
  readonly code: string;
  readonly message: string;
  readonly severity: 'error' | 'warning';
}

export interface CreatedJsonBackup {
  readonly canonical_json: string;
  readonly counts: JsonBackupCounts;
  readonly export_id: string;
  readonly exported_at: string;
  readonly file_name: string;
  readonly whole_file_sha256: string;
}

export interface CurrentJsonBackupVerification {
  readonly counts: JsonBackupCounts;
  readonly export_id: string;
  readonly issues: readonly JsonBackupIssue[];
  readonly matches_current_data: boolean;
  readonly whole_file_sha256: string;
}

export interface JsonRestorePreview {
  readonly counts: JsonBackupCounts;
  readonly duplicate_entity_ids: readonly string[];
  readonly export_id: string;
  readonly issues: readonly JsonBackupIssue[];
  readonly whole_file_sha256: string;
}

export interface JsonRestoreResult {
  readonly reopen_required: boolean;
  readonly replacement_dataset_epoch: string;
}

export interface PreparedJsonBackup extends CreatedJsonBackup {
  readonly covered_mutation_generation: string;
  readonly verified_file: VerifiedExportFile;
}

export function prepareJsonBackup(input: {
  readonly build_metadata: BuildMetadata;
  readonly now: string;
  readonly payload_snapshot: LocalBackupPayloadSnapshot;
  readonly uuid: string;
}): PreparedJsonBackup {
  const envelope = createExportEnvelope({
    contract_version: 'export_envelope.v1',
    schema_version: '1.0.0',
    canonical_json_version: 'dmf-canonical-json.v1',
    payload_digest_contract: 'dmf.export-payload.sha256.v1',
    export_id: input.uuid,
    exported_at: input.now,
    created_by_build: input.build_metadata,
    payload: input.payload_snapshot.payload,
  });
  const verifiedFile = createExportEnvelopeFile(envelope);
  assertBackupByteLength(new TextEncoder().encode(verifiedFile.canonical_json));
  return {
    canonical_json: verifiedFile.canonical_json,
    counts: jsonBackupCounts(envelope.payload),
    covered_mutation_generation: input.payload_snapshot.covered_mutation_generation,
    export_id: envelope.export_id,
    exported_at: envelope.exported_at,
    file_name: backupFileName(envelope.exported_at, envelope.export_id),
    verified_file: verifiedFile,
    whole_file_sha256: verifiedFile.whole_file_sha256,
  };
}

export function parseBoundedJsonBackup(bytes: Uint8Array): VerifiedExportFile {
  assertBackupByteLength(bytes);
  return parseExportEnvelopeFile(bytes);
}

export function jsonBackupCounts(payloadInput: ExportPayload): JsonBackupCounts {
  const payload = exportPayloadSchema.parse(payloadInput);
  return {
    shifts: payload.shifts.length,
    trip_segments: payload.shifts.reduce(
      (count, aggregate) => count + aggregate.segments.length,
      0,
    ),
    trip_revisions: payload.trip_revisions.length,
    vehicles: payload.vehicles.length,
    route_variants: payload.route_variants.length,
    route_templates: payload.route_templates.length,
    user_defined_locations: payload.user_defined_locations.length,
    location_merge_decisions: payload.location_merge_decisions.length,
  };
}

export function payloadCoverageSha256(payloadInput: ExportPayload): string {
  const payload = exportPayloadSchema.parse(payloadInput);
  const normalized = {
    ...payload,
    local_profile: {
      ...payload.local_profile,
      // Verification evidence necessarily describes a file that already
      // exists, so the file cannot recursively contain evidence about itself.
      last_verified_export: null,
    },
  };
  return sha256CanonicalJson('dmf.export-payload.sha256.v1', normalized satisfies JsonValue);
}

export function backupMatchesCurrentPayload(
  verifiedFile: VerifiedExportFile,
  currentPayload: ExportPayload,
): boolean {
  return (
    verifiedFile.envelope.payload.local_profile.local_profile_id ===
      currentPayload.local_profile.local_profile_id &&
    verifiedFile.envelope.payload.dataset_epoch.dataset_epoch ===
      currentPayload.dataset_epoch.dataset_epoch &&
    payloadCoverageSha256(verifiedFile.envelope.payload) === payloadCoverageSha256(currentPayload)
  );
}

export function createLocalRestorePreview(
  verifiedFile: VerifiedExportFile,
  currentPayloadInput: ExportPayload,
) {
  const currentPayload = exportPayloadSchema.parse(currentPayloadInput);
  const sourcePayload = verifiedFile.envelope.payload;
  const targetIds = new Set(exportPayloadEntityIds(currentPayload));
  const duplicateEntityIds = [...new Set(exportPayloadEntityIds(sourcePayload))]
    .filter((entityId) => targetIds.has(entityId))
    .sort();
  const issues = [];
  if (
    sourcePayload.sync_metadata.pending_outbox.length > 0 ||
    sourcePayload.sync_metadata.device_cursor !== null ||
    sourcePayload.sync_metadata.last_server_cursor !== null
  ) {
    issues.push({
      severity: 'warning' as const,
      code: 'invalid_record' as const,
      entity_type: null,
      entity_id: null,
      message_key: 'import.local_alpha.sync_metadata_not_activated',
    });
  }
  return importPreviewSchema.parse({
    source_export_id: verifiedFile.envelope.export_id,
    source_export_sha256: verifiedFile.whole_file_sha256,
    supported_schema_version: verifiedFile.envelope.schema_version,
    counts: jsonBackupCounts(sourcePayload),
    duplicate_entity_ids: duplicateEntityIds,
    issues,
    active_data_unchanged: true,
  });
}

export function restorePreviewForUi(
  verifiedFile: VerifiedExportFile,
  preview: ReturnType<typeof createLocalRestorePreview>,
): JsonRestorePreview {
  return {
    counts: preview.counts,
    duplicate_entity_ids: preview.duplicate_entity_ids,
    export_id: verifiedFile.envelope.export_id,
    issues: preview.issues.map(importIssueForUi),
    whole_file_sha256: verifiedFile.whole_file_sha256,
  };
}

export function createLocalReplaceExecution(input: {
  readonly checkpoint: LocalReplacementCheckpointEvidence;
  readonly current_payload: ExportPayload;
  readonly expected_whole_file_sha256: string;
  readonly import_plan_id: string;
  readonly now: string;
  readonly replacement_dataset_epoch: string;
  readonly verified_file: VerifiedExportFile;
}) {
  if (input.verified_file.whole_file_sha256 !== input.expected_whole_file_sha256) {
    throw new Error('Znovu vybraný soubor neodpovídá souboru z náhledu.');
  }
  if (input.current_payload.local_profile.account_identity !== null) {
    throw new Error('Lokální obnova je povolena pouze pro profil bez cloudového účtu.');
  }
  const preview = createLocalRestorePreview(input.verified_file, input.current_payload);
  const plan = replaceImportPlanSchema.parse({
    contract_version: 'import_plan.v1',
    import_plan_id: input.import_plan_id,
    source_export_id: input.verified_file.envelope.export_id,
    source_export_sha256: input.verified_file.whole_file_sha256,
    source_dataset_epoch: input.verified_file.envelope.payload.dataset_epoch.dataset_epoch,
    target_dataset_epoch: input.current_payload.dataset_epoch.dataset_epoch,
    prepared_at: input.now,
    preview,
    explicit_user_confirmation_at: input.now,
    mode: 'replace',
    execution_scope: 'local_profile',
    target_account_binding: 'unbound',
    connectivity: 'offline',
    restore_checkpoint: checkpointForContract(input.checkpoint),
    replacement_dataset_epoch: input.replacement_dataset_epoch,
    invalidate_previous_outbox: true,
    force_other_devices_full_resync: false,
    external_rescue_evidence: {
      initially_exported_sha256: input.expected_whole_file_sha256,
      reselected_file_sha256: input.verified_file.whole_file_sha256,
      reselected_and_verified_at: input.now,
    },
  });
  const decision = evaluateImportExecution(plan);
  if (!decision.executable) {
    throw new Error(`Obnova není spustitelná: ${decision.reason}.`);
  }
  return {
    authorization: {
      execution_scope: 'local_profile' as const,
      target_account_binding: 'unbound' as const,
      connectivity: 'offline' as const,
      restore_checkpoint: checkpointForContract(input.checkpoint),
      invalidate_previous_outbox: true as const,
      force_other_devices_full_resync: false as const,
    },
    dataset_epoch: replacementDatasetEpoch(plan),
    plan,
    preview,
    source_payload: input.verified_file.envelope.payload,
  };
}

function checkpointForContract(checkpoint: LocalReplacementCheckpointEvidence) {
  return {
    checkpoint_id: checkpoint.checkpoint_id,
    checkpoint_kind: checkpoint.checkpoint_kind,
    dataset_epoch: checkpoint.dataset_epoch,
    payload_sha256: checkpoint.payload_sha256,
    created_at: checkpoint.created_at,
    immutable: checkpoint.immutable,
    validation_status: checkpoint.validation_status,
    retention_state: checkpoint.retention_state,
  };
}

function exportPayloadEntityIds(payload: ExportPayload): string[] {
  return [
    ...payload.shifts.map((item) => item.shift.shift_id),
    ...payload.shifts.flatMap((item) => item.segments.map((segment) => segment.trip_segment_id)),
    ...payload.trip_revisions.map((item) => item.trip_revision_id),
    ...payload.vehicles.map((item) => item.vehicle_id),
    ...payload.route_variants.map((item) => item.route_variant_id),
    ...payload.route_templates.map((item) => item.route_template_id),
    ...payload.user_defined_locations.map((item) => item.location_id),
    ...payload.location_merge_decisions.map((item) => item.decision_id),
    payload.user_preferences.user_preferences_id,
  ];
}

function importIssueForUi(issue: {
  readonly code: string;
  readonly message_key: string;
  readonly severity: 'conflict' | 'error' | 'warning';
}): JsonBackupIssue {
  const message =
    issue.message_key === 'import.local_alpha.sync_metadata_not_activated'
      ? 'Synchronizační fronta a zařízení jsou v souboru pouze pro audit; místní obnova je neaktivuje.'
      : `Import hlásí problém ${issue.message_key}.`;
  return {
    code: issue.code,
    message,
    severity: issue.severity === 'warning' ? 'warning' : 'error',
  };
}

function assertBackupByteLength(bytes: Uint8Array): void {
  if (bytes.byteLength > MAX_LOCAL_ALPHA_BACKUP_BYTES) {
    throw new Error('JSON záloha překračuje bezpečný limit 32 MiB pro tuto verzi aplikace.');
  }
}

function backupFileName(exportedAt: string, exportId: string): string {
  const calendarDate = exportedAt.slice(0, 10);
  return `der-maschinfuehrer-backup-${calendarDate}-${exportId}.json`;
}
